import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number | null} */
  #reservationTimer = null;

  /** @type {Promise<number | null> | null} */
  #insuranceVariantPromise = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleRemoveLeakedScriptText);
    this.addEventListener(DialogOpenEvent.eventName, this.#loadRecommendations);
    this.addEventListener(DialogOpenEvent.eventName, this.#startReservationTimer);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleCheckoutClick);
    this.addEventListener('click', this.#handleRecommendationsScrollClick);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleRemoveLeakedScriptText);
    this.removeEventListener(DialogOpenEvent.eventName, this.#loadRecommendations);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startReservationTimer);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleCheckoutClick);
    this.removeEventListener('click', this.#handleRecommendationsScrollClick);
    this.#historyAbortController?.abort();
    this.#stopReservationTimer();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer for successful add-to-cart actions and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    const source = event.detail.data?.source;
    const shouldOpenDrawer =
      !event.detail.data?.didError &&
      (source === 'product-form-component' || source === 'cart-drawer-recommendations');

    if (shouldOpenDrawer) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
    this.#startReservationTimer();
    this.#scheduleRemoveLeakedScriptText();

    if (this.refs.dialog?.open) {
      setTimeout(this.#loadRecommendations, 150);
    }
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #startReservationTimer = () => {
    const timer = this.refs.dialog?.querySelector('[data-cart-reservation-time]');
    if (!(timer instanceof HTMLElement)) return;

    const storageKey = 'purecloCartReservationExpiresAt';
    const duration = 10 * 60 * 1000;
    const now = Date.now();
    let expiresAt = Number(window.localStorage.getItem(storageKey));

    if (!expiresAt || expiresAt <= now) {
      expiresAt = now + duration;
      window.localStorage.setItem(storageKey, expiresAt.toString());
    }

    this.#stopReservationTimer();

    const render = () => {
      const remaining = Math.max(0, expiresAt - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      timer.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

      if (remaining <= 0) {
        expiresAt = Date.now() + duration;
        window.localStorage.setItem(storageKey, expiresAt.toString());
      }
    };

    render();
    this.#reservationTimer = window.setInterval(render, 1000);
  };

  #stopReservationTimer() {
    if (this.#reservationTimer === null) return;
    window.clearInterval(this.#reservationTimer);
    this.#reservationTimer = null;
  }

  /**
   * @param {Event} event
   */
  #handleCheckoutClick = async (event) => {
    const checkoutButton = event.target instanceof Element ? event.target.closest('[data-cart-drawer-checkout]') : null;
    if (!(checkoutButton instanceof HTMLButtonElement) || checkoutButton.dataset.loading === 'true') return;

    event.preventDefault();
    event.stopPropagation();

    checkoutButton.dataset.loading = 'true';
    checkoutButton.disabled = true;

    try {
      window.__purecloPreparingCheckoutInsurance = true;
      const cart = await this.#fetchCart();
      await this.#ensureInsuranceProduct(cart, checkoutButton.dataset.insuranceHandle);
      const updatedCart = await this.#fetchCart();
      window.location.href = this.#getCheckoutUrl(updatedCart, checkoutButton);
    } catch (error) {
      console.warn('[Cart drawer checkout]', error);
      const form = document.getElementById(checkoutButton.getAttribute('form') || 'cart-form');
      if (form instanceof HTMLFormElement) {
        form.submit();
      } else {
        window.location.href = `${this.#getRoutesRoot()}checkout`;
      }
    } finally {
      window.setTimeout(() => {
        window.__purecloPreparingCheckoutInsurance = false;
      }, 3000);
      checkoutButton.dataset.loading = 'false';
      checkoutButton.disabled = false;
    }
  };

  async #fetchCart() {
    const response = await fetch(`${this.#getRoutesRoot()}cart.js`, { credentials: 'same-origin' });
    return response.json();
  }

  /**
   * @param {any} cart
   * @param {string | undefined} handle
   */
  async #ensureInsuranceProduct(cart, handle) {
    if (!handle || !cart?.items?.length) return;

    const hasVisibleItems = cart.items.some((item) => item.handle !== handle);
    if (!hasVisibleItems) return;

    const variantId = await this.#getInsuranceVariantId(handle);
    if (!variantId) return;

    const insuranceItems = cart.items.filter((item) => item.handle === handle || item.variant_id?.toString() === variantId.toString());

    for (const item of insuranceItems) {
      await this.#changeInsuranceProductQuantity(item, 0);
    }

    await this.#addVisibleInsuranceProduct(variantId);
    await this.#setCheckoutProtectionVisible(true);
  }

  /**
   * @param {number | string} variantId
   */
  async #addVisibleInsuranceProduct(variantId) {
    await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
          },
        ],
      }),
    });
  }

  /**
   * @param {boolean} visible
   */
  async #setCheckoutProtectionVisible(visible) {
    await fetch(this.#getCartUpdateUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          _checkout_protection_visible: visible ? 'true' : '',
        },
      }),
    });
  }

  /**
   * @param {{ key?: string, variant_id?: number }} item
   * @param {number} quantity
   */
  async #changeInsuranceProductQuantity(item, quantity) {
    const changeUrl = Theme.routes.cart_change_url.endsWith('.js')
      ? Theme.routes.cart_change_url
      : `${Theme.routes.cart_change_url}.js`;

    await fetch(changeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: item.key || item.variant_id,
        quantity,
      }),
    });
  }

  /**
   * @param {string} handle
   */
  #getInsuranceVariantId(handle) {
    if (this.#insuranceVariantPromise) return this.#insuranceVariantPromise;

    this.#insuranceVariantPromise = fetch(`${this.#getRoutesRoot()}products/${handle}.js`, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        const variant = product?.variants?.find((item) => item.available) || product?.variants?.[0];
        return variant?.id || null;
      })
      .catch((error) => {
        console.warn('[Cart drawer insurance product]', error);
        return null;
      });

    return this.#insuranceVariantPromise;
  }

  /**
   * @param {any} cart
   * @param {HTMLButtonElement} checkoutButton
   */
  #getCheckoutUrl(cart, checkoutButton) {
    const threshold = Number.parseInt(checkoutButton.dataset.multiDiscountThreshold || '12000', 10);
    const discountCode = checkoutButton.dataset.multiDiscountCode || '';
    const root = this.#getRoutesRoot();
    const cartSubtotal = cart?.items_subtotal_price ?? cart?.total_price ?? 0;
    const qualifiesForDiscount = discountCode && cartSubtotal >= threshold;

    if (!qualifiesForDiscount) return `${root}checkout`;

    const redirect = encodeURIComponent(`${root}checkout`);
    return `${root}discount/${encodeURIComponent(discountCode)}?redirect=${redirect}`;
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  #loadRecommendations = async () => {
    const dialog = this.refs.dialog;
    const wrapper = dialog?.querySelector('[data-cart-drawer-recommendations]');
    const slot = dialog?.querySelector('[data-cart-drawer-recommendations-slot]');
    if (!(wrapper instanceof HTMLElement) || !(slot instanceof HTMLElement) || slot.dataset.loading === 'true') return;

    if (wrapper.hasAttribute('data-cart-drawer-recommendations-static')) {
      wrapper.hidden = !slot.textContent?.trim();
      this.#normalizeRecommendationButtons(slot);
      this.#bindRecommendationQuickAdd(slot);
      return;
    }

    slot.dataset.loading = 'true';

    try {
      const cartResponse = await fetch(`${this.#getRoutesRoot()}cart.js`, { credentials: 'same-origin' });
      const cart = await cartResponse.json();
      const baseItem = cart?.items
        ?.slice()
        .reverse()
        .find((item) => item.handle !== 'extra-priority-when-packing-100-insurance-1');

      if (!baseItem?.product_id) {
        wrapper.hidden = true;
        slot.innerHTML = '';
        return;
      }

      const parsedLimit = Number.parseInt(wrapper.dataset.recommendationsLimit || '8', 10);
      const limit = Number.isNaN(parsedLimit) ? 8 : Math.max(parsedLimit, 1);

      let html = await this.#fetchRecommendationsHTML(baseItem.product_id, limit, 'related');
      let inner = this.#extractRecommendationsInner(html);

      if (!inner.trim()) {
        html = await this.#fetchRecommendationsHTML(baseItem.product_id, limit, 'complementary');
        inner = this.#extractRecommendationsInner(html);
      }

      if (this.#isUnsafeHTML(inner)) {
        wrapper.hidden = true;
        slot.innerHTML = '';
        return;
      }

      wrapper.hidden = !inner.trim();
      slot.innerHTML = inner;
      this.#removeLeakedScriptText();
      this.#normalizeRecommendationButtons(slot);
      this.#bindRecommendationQuickAdd(slot);
    } catch (error) {
      console.warn('[Cart drawer recommendations]', error);
    } finally {
      slot.dataset.loading = 'false';
      this.#scheduleRemoveLeakedScriptText();
    }
  };

  /**
   * @param {Event} event
   */
  #handleRecommendationsScrollClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-cart-recommendations-scroll]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const scroller = this.refs.dialog?.querySelector('[data-cart-recommendations-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    const direction = Number.parseInt(button.dataset.cartRecommendationsScroll || '1', 10);
    const card = scroller.querySelector('.cart-drawer-related-recommendations__card');
    const amount = card instanceof HTMLElement ? card.getBoundingClientRect().width : scroller.clientWidth * 0.75;

    scroller.scrollBy({
      left: amount * (Number.isNaN(direction) ? 1 : direction),
      behavior: 'smooth',
    });
  };

  /**
   * @param {number|string} productId
   * @param {number} limit
   * @param {'related'|'complementary'} intent
   */
  async #fetchRecommendationsHTML(productId, limit, intent) {
    const searchParams = new URLSearchParams({
      section_id: 'cart-drawer-related-recommendations',
      product_id: productId.toString(),
      limit: limit.toString(),
      intent,
    });

    const response = await fetch(`${this.#getRoutesRoot()}recommendations/products?${searchParams}`, {
      credentials: 'same-origin',
    });

    return response.text();
  }

  /**
   * @param {string} html
   */
  #extractRecommendationsInner(html) {
    if (this.#isUnsafeHTML(html)) return '';

    const doc = document.implementation.createHTMLDocument('');
    doc.documentElement.innerHTML = html;

    const section =
      doc.querySelector('#shopify-section-cart-drawer-related-recommendations .cart-drawer-related-recommendations') ||
      doc.querySelector('.cart-drawer-related-recommendations');

    return section?.innerHTML || '';
  }

  /**
   * @param {string} html
   */
  #isUnsafeHTML(html) {
    return (
      html.includes('import { DialogComponent') ||
      html.includes('class CartDrawerComponent') ||
      html.includes('#handleHistoryClose') ||
      html.includes('#handlePopState') ||
      html.includes('#handleCartAdd') ||
      html.includes("customElements.define('cart-drawer-component'") ||
      html.includes('customElements.define("cart-drawer-component"')
    );
  }

  #scheduleRemoveLeakedScriptText = () => {
    this.#removeLeakedScriptText();
    window.setTimeout(() => this.#removeLeakedScriptText(), 100);
    window.setTimeout(() => this.#removeLeakedScriptText(), 500);
    window.setTimeout(() => this.#removeLeakedScriptText(), 1200);
  };

  #removeLeakedScriptText() {
    const root = this.refs.dialog;
    if (!root) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const leakedNodes = [];

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const text = node.textContent || '';
      if (this.#isUnsafeHTML(text)) {
        leakedNodes.push(node);
      }
    }

    for (const node of leakedNodes) {
      const parent = node.parentElement;
      if (parent && parent.childNodes.length === 1) {
        parent.remove();
      } else {
        node.remove();
      }
    }
  }

  /**
   * @param {HTMLElement} root
   */
  #normalizeRecommendationButtons(root) {
    const chooseLinks = root.querySelectorAll('a.cart-drawer-related-recommendations__button');

    for (const link of chooseLinks) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const handle = link.pathname.split('/products/')[1]?.split('/')[0];
      if (!handle) continue;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = link.className;
      button.dataset.cartRecommendationChoose = '';
      button.dataset.productHandle = handle;
      button.innerHTML = '<span class="button-text">+ADD</span>';
      link.replaceWith(button);
    }

    root.querySelectorAll('.cart-drawer-related-recommendations__button .button-text').forEach((item) => {
      if (item.textContent?.trim().toLowerCase() === 'choose') item.textContent = '+ADD';
    });
  }

  /**
   * @param {HTMLElement} root
   */
  #bindRecommendationQuickAdd(root) {
    const buttons = root.querySelectorAll('[data-cart-recommendation-add], [data-cart-recommendation-choose]');

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement) || button.dataset.bound === 'true') continue;
      button.dataset.bound = 'true';

      button.addEventListener('click', async (event) => {
        event.preventDefault();

        const productHandle = button.getAttribute('data-product-handle');
        const fallbackVariantId = button.getAttribute('data-variant-id');
        if (!productHandle && !fallbackVariantId) return;

        const previousHTML = button.innerHTML;
        button.disabled = true;

        try {
          if (productHandle) {
            const product = await this.#fetchProduct(productHandle);
            if (product) {
              this.#openRecommendationQuickAdd(product);
              return;
            }
          }

          if (fallbackVariantId) await this.#addRecommendationVariant(Number(fallbackVariantId));
        } catch (error) {
          console.warn('[Cart drawer recommendation add]', error);
        } finally {
          button.disabled = false;
          button.innerHTML = previousHTML;
        }
      });
    }
  }

  /**
   * @param {string} handle
   */
  async #fetchProduct(handle) {
    const response = await fetch(`${this.#getRoutesRoot()}products/${handle}.js`, { credentials: 'same-origin' });
    return response.ok ? response.json() : null;
  }

  /**
   * @param {any} product
   */
  #openRecommendationQuickAdd(product) {
    const options = this.#getProductOptions(product);
    const selectedOptions = [];

    for (const option of options) {
      const availableVariant = product.variants?.find((variant) => variant.available && variant[`option${option.index + 1}`]);
      selectedOptions[option.index] = availableVariant?.[`option${option.index + 1}`] || option.values?.[0] || '';
    }

    const overlay = document.createElement('div');
    overlay.className = 'cart-drawer-quick-add';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = this.#getQuickAddHTML(product, selectedOptions);

    (this.refs.dialog || document.body).appendChild(overlay);
    this.#syncQuickAddState(overlay, product, selectedOptions);

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target instanceof Element && event.target.closest('[data-quick-add-close]')) {
        overlay.remove();
      }
    });

    overlay.addEventListener('click', async (event) => {
      const choice = event.target instanceof Element ? event.target.closest('[data-option-index]') : null;
      if (choice instanceof HTMLButtonElement) {
        const optionIndex = Number.parseInt(choice.dataset.optionIndex || '0', 10);
        selectedOptions[optionIndex] = choice.dataset.optionValue || '';
        this.#syncQuickAddState(overlay, product, selectedOptions);
      }

      const submit = event.target instanceof Element ? event.target.closest('[data-quick-add-submit]') : null;
      if (!(submit instanceof HTMLButtonElement) || submit.disabled) return;

      const variant = this.#findVariant(product, selectedOptions);
      if (!variant) return;

      submit.disabled = true;
      await this.#addRecommendationVariant(variant.id);
      overlay.remove();
    });
  }

  /**
   * @param {any} product
   * @param {string[]} selectedOptions
   */
  #getQuickAddHTML(product, selectedOptions) {
    const title = this.#escapeHTML(product.title || '');
    const image = product.featured_image || product.images?.[0] || '';
    const price = this.#formatMoney(product.price || product.variants?.[0]?.price || 0);
    const options = this.#getProductOptions(product);

    return `
      <div class="cart-drawer-quick-add__dialog">
        <button type="button" class="cart-drawer-quick-add__close" data-quick-add-close aria-label="Close">×</button>
        <div class="cart-drawer-quick-add__summary">
          ${image ? `<img class="cart-drawer-quick-add__image" src="${image}" alt="${title}">` : '<span class="cart-drawer-quick-add__image"></span>'}
          <div>
            <h3 class="cart-drawer-quick-add__title">${title}</h3>
            <p class="cart-drawer-quick-add__price">${price}</p>
          </div>
        </div>
        <div class="cart-drawer-quick-add__form">
          ${options.map((option) => this.#getQuickAddOptionHTML(product, option, selectedOptions[option.index])).join('')}
          <button type="button" class="cart-drawer-quick-add__submit" data-quick-add-submit>ADD TO CART</button>
        </div>
      </div>
    `;
  }

  /**
   * @param {any} product
   * @param {{ name: string, index: number, values: string[] }} option
   * @param {string} selectedValue
   */
  #getQuickAddOptionHTML(product, option, selectedValue) {
    const values = option.values?.length
      ? option.values
      : Array.from(new Set(product.variants.map((variant) => variant[`option${option.index + 1}`]).filter(Boolean)));
    const isColor = /colo(u)?r|cor/i.test(option.name);

    return `
      <fieldset class="cart-drawer-quick-add__fieldset">
        <legend class="cart-drawer-quick-add__label">
          ${this.#escapeHTML(option.name)}:<span class="cart-drawer-quick-add__value" data-selected-option="${option.index}">${this.#escapeHTML(selectedValue || values[0] || '')}</span>
        </legend>
        <div class="cart-drawer-quick-add__choices">
          ${values.map((value) => {
            const variant = product.variants.find((item) => item[`option${option.index + 1}`] === value && item.featured_image) || product.variants.find((item) => item[`option${option.index + 1}`] === value);
            const image = isColor && variant?.featured_image?.src ? variant.featured_image.src : '';
            return `
              <button
                type="button"
                class="cart-drawer-quick-add__choice${image ? ' cart-drawer-quick-add__choice--image' : ''}"
                data-option-index="${option.index}"
                data-option-value="${this.#escapeAttribute(value)}"
                aria-pressed="${value === selectedValue}"
              >
                ${image ? `<img src="${image}" alt="${this.#escapeAttribute(value)}">` : this.#escapeHTML(value)}
              </button>
            `;
          }).join('')}
        </div>
      </fieldset>
    `;
  }

  /**
   * @param {any} product
   */
  #getProductOptions(product) {
    return (product.options || [])
      .map((option, index) => ({
        name: typeof option === 'string' ? option : option?.name || '',
        index,
        values: Array.isArray(option?.values) ? option.values : [],
      }))
      .filter((option) => {
        const values = option.values.length
          ? option.values
          : Array.from(new Set(product.variants.map((variant) => variant[`option${option.index + 1}`]).filter(Boolean)));
        return !(option.name.toLowerCase() === 'title' && values.length === 1 && values[0].toLowerCase() === 'default title');
      });
  }

  /**
   * @param {HTMLElement} overlay
   * @param {any} product
   * @param {string[]} selectedOptions
   */
  #syncQuickAddState(overlay, product, selectedOptions) {
    overlay.querySelectorAll('[data-selected-option]').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      const optionIndex = Number.parseInt(node.dataset.selectedOption || '0', 10);
      node.textContent = selectedOptions[optionIndex] || '';
    });

    overlay.querySelectorAll('[data-option-index]').forEach((node) => {
      if (!(node instanceof HTMLButtonElement)) return;
      const optionIndex = Number.parseInt(node.dataset.optionIndex || '0', 10);
      node.setAttribute('aria-pressed', node.dataset.optionValue === selectedOptions[optionIndex] ? 'true' : 'false');
    });

    const variant = this.#findVariant(product, selectedOptions);
    const submit = overlay.querySelector('[data-quick-add-submit]');
    if (submit instanceof HTMLButtonElement) submit.disabled = !variant?.available;
  }

  /**
   * @param {any} product
   * @param {string[]} selectedOptions
   */
  #findVariant(product, selectedOptions) {
    return product.variants?.find((variant) =>
      selectedOptions.every((value, index) => !value || variant[`option${index + 1}`] === value)
    );
  }

  /**
   * @param {number} variantId
   */
  async #addRecommendationVariant(variantId) {
    const sectionIds = this.#getCartItemsSectionIds();
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const json = await response.json();

    if (json.status) throw json;

    const cartResponse = await fetch(`${this.#getRoutesRoot()}cart.js`, { credentials: 'same-origin' });
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart ?? undefined, 'cart-drawer-recommendations', {
        source: 'cart-drawer-recommendations',
        itemCount: 1,
        variantId,
        sections: json.sections || {},
      })
    );

    if (!json.sections || sectionIds.some((sectionId) => !json.sections?.[sectionId])) {
      document.dispatchEvent(
        new CartAddEvent(cart ?? undefined, 'cart-drawer-recommendations-refresh', {
          source: 'cart-drawer-recommendations',
          itemCount: 0,
          variantId,
          sections: {},
        })
      );
    }

    await this.#loadRecommendations();
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
      maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
    }).format(value);
  }

  /**
   * @param {string} value
   */
  #escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  /**
   * @param {string} value
   */
  #escapeAttribute(value) {
    return this.#escapeHTML(value).replaceAll('"', '&quot;');
  }

  #getCartItemsSectionIds() {
    const ids = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.add(item.dataset.sectionId);
      }
    }

    return Array.from(ids);
  }

  #getRoutesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  #getCartUpdateUrl() {
    return Theme.routes.cart_update_url.endsWith('.js') ? Theme.routes.cart_update_url : `${Theme.routes.cart_update_url}.js`;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
